import { createSelector } from '@reduxjs/toolkit';
import { useMemo } from 'preact/hooks';

import { ParsedUserProduct } from '@/queries/products/types';
import { AppState, useSelector } from '@/redux/helper';

import { getBundlingZooId, getUserProductImageVariant } from './utils';

export interface UserProductDisplay {
  imageURL?: string;
  title?: string;
  variant: ReturnType<typeof getUserProductImageVariant>;
  displayTitleName?: string;
  habitatId?: string;
}

export const useUserProductDisplay = (userProduct: ParsedUserProduct): UserProductDisplay => {
  const source = userProduct.product?.source;
  const isBundling = source === 'bundling';
  const isStripe = source === 'stripe';
  const zooId = isBundling ? getBundlingZooId(userProduct.product?.metadata) : undefined;
  const habitatId = isStripe ? userProduct.associatedHabitatIds?.[0] : undefined;

  const selectStripeHabitat = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (allHabitats) => {
          if (!habitatId) return undefined;
          const habitat = allHabitats.find((h) => h._id === habitatId);
          return habitat
            ? {
                imageURL: habitat.profileImage,
                title: habitat.animal,
              }
            : undefined;
        },
      ),
    [habitatId],
  );

  const selectBundlingZooDisplay = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (allHabitats) => {
          if (!zooId) return undefined;
          const habitat = allHabitats.find((h) => h.zoo._id === zooId);
          return habitat
            ? {
                imageURL: habitat.zoo.logo,
                title: habitat.zoo.name,
              }
            : undefined;
        },
      ),
    [zooId],
  );

  const stripeHabitat = useSelector(selectStripeHabitat);
  const bundlingZooDisplay = useSelector(selectBundlingZooDisplay);

  const display = isBundling ? bundlingZooDisplay : stripeHabitat;
  const displayTitleName = isBundling && display?.title ? display.title : stripeHabitat?.title;

  return {
    imageURL: display?.imageURL,
    title: display?.title,
    variant: getUserProductImageVariant(source),
    displayTitleName,
    habitatId,
  };
};
